import type { TeamMemberResponse } from '@vagusx/clients';

/**
 * Local draft representation of a team member. Mirrors enough of
 * `TeamMemberResponse` for rendering, but lives in component state so the
 * user can stage adds + removes before clicking Save. New additions have
 * no server id — we generate a `draftId` for React keys; the server
 * never sees it.
 */
export interface DraftMember {
  draftId: string;
  memberType: 'user' | 'agent';
  userId?: string;
  agentId?: string;
  display: { name: string; email?: string };
}

/**
 * Rehydrate a DraftMember from a server-issued member record.
 * The server row's id becomes the draftId so React's keyed rendering
 * stays stable across view → edit → save cycles.
 */
export function toDraft(m: TeamMemberResponse): DraftMember {
  return {
    draftId: m.id,
    memberType: m.memberType,
    userId: m.userId,
    agentId: m.agentId,
    display: {
      name: m.user?.name ?? m.agent?.name ?? '?',
      email: m.user?.email,
    },
  };
}

/**
 * Generate a stable draft id for an unsaved member. Prefers
 * `crypto.randomUUID()` when available (all modern browsers); falls back
 * to a timestamp+random composite so it still works under older runtimes
 * or test harnesses that don't polyfill.
 */
export function makeDraftId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `draft-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
